import { cva } from 'class-variance-authority';
import { ChangeEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';

interface ICheckboxProps {
  label?: string | null;
  name?: string;
  checked?: boolean;
  parentClass?: string;
  labelClass?: string;
  inputClass?: string;
  disabled?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onClick?: any;
}

const checkboxWrapperStyles = cva(
  'mr-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all duration-150',
  {
    variants: {
      checked: {
        true: 'border-primary-700 bg-primary-700',
        false: '',
      },
      disabled: {
        true: '!border-black-300 bg-black-100',
        false:
          'border-grey-300 group-hover/checkbox:border-primary-600 hover:border-blue-700 hover:bg-blue-100',
      },
    },
    compoundVariants: [
      {
        checked: false,
        disabled: true,
        className: '!border-black-300 bg-black-100',
      },
    ],
    defaultVariants: {
      checked: false,
      disabled: false,
    },
  },
);

export default function Checkbox({
  label,
  name,
  checked = false,
  parentClass,
  labelClass,
  inputClass,
  disabled,
  onChange,
  onClick,
}: ICheckboxProps) {
  return (
    <div
      className={`group/checkbox hover:bg-black-50 active:bg-black-200 relative flex items-center rounded-md px-4 text-sm font-normal transition-all duration-150 ${parentClass} ${
        disabled && 'cursor-not-allowed hover:!bg-inherit'
      }`}
    >
      <input
        type="checkbox"
        name={name}
        id={name}
        value=""
        className={`absolute h-5 w-5 opacity-0 ${
          disabled && 'cursor-not-allowed'
        }`}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={twMerge(
          checkboxWrapperStyles({ checked, disabled }),
          inputClass,
        )}
      >
        <svg
          className={`pointer-events-none h-3 w-3 transition-opacity duration-150 ${
            checked ? 'opacity-100' : 'opacity-0'
          } ${disabled && 'cursor-not-allowed'}`}
          viewBox="0 0 16 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M1 6L5.5 10.5L15 1"
            stroke={disabled ? 'var(--color-black-300)' : '#FFFFFF'}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={
              checked
                ? 'animate-[checkbox-tick-draw_0.3s_ease-out_forwards]'
                : ''
            }
            style={{
              strokeDasharray: '24',
              strokeDashoffset: checked ? '0' : '24',
            }}
          />
        </svg>
      </div>
      {label && (
        <label
          htmlFor={name}
          className={`text-black-700 block w-full cursor-pointer py-[9px] select-none ${
            disabled
              ? '!text-black-400 cursor-not-allowed'
              : 'group-hover/checkbox:text-black-900'
          } ${labelClass}`}
          onClick={onClick}
        >
          {label}
        </label>
      )}
    </div>
  );
}
